import { useEffect, useRef } from 'react';
import { getColorScheme, getIndustryProfile } from '../data/industryProfiles';
import { BRAND } from '../config/brand';
import './IcpFormPreview.css';

// The mockup is authored at a fixed 980px design width. In "band" placement we
// scale it to exactly fill its container by measuring the container and setting
// --ifp-band-scale = width / 980. (CSS can't do this: dividing a length by a
// length in calc() is invalid, which is why the old cqw approach silently failed.)
const DESIGN_WIDTH = 980;

const INDUSTRY_ICON_MAP = {
  dental: 'fa-tooth',
  plumbing: 'fa-faucet-drip',
  hvac: 'fa-fan',
  restaurant: 'fa-utensils',
  legal: 'fa-scale-balanced',
  fitness: 'fa-dumbbell',
  real_estate: 'fa-house',
  auto_repair: 'fa-car-side',
  salon: 'fa-spa',
  consulting: 'fa-briefcase',
  technology: 'fa-microchip',
  ecommerce: 'fa-bag-shopping',
  medical: 'fa-stethoscope',
  construction: 'fa-helmet-safety',
  default: 'fa-globe',
};

const INDUSTRY_FONT_MAP = {
  dental: '"Bricolage Grotesque", sans-serif',
  plumbing: '"Sora", sans-serif',
  hvac: '"Space Grotesk", sans-serif',
  restaurant: '"Outfit", sans-serif',
  legal: '"Plus Jakarta Sans", sans-serif',
  fitness: '"Unbounded", sans-serif',
  real_estate: '"Outfit", sans-serif',
  auto_repair: '"Space Grotesk", sans-serif',
  salon: '"Bricolage Grotesque", sans-serif',
  consulting: '"Plus Jakarta Sans", sans-serif',
  technology: '"Space Grotesk", sans-serif',
  ecommerce: '"Outfit", sans-serif',
  medical: '"Plus Jakarta Sans", sans-serif',
  construction: '"Sora", sans-serif',
  default: '"Manrope", sans-serif',
};

function IcpFormPreview({ domain, company, industry, location, visible = false, mode = 'backdrop', placement = 'overlay' }) {
  const rootRef = useRef(null);

  useEffect(() => {
    if (placement !== 'band') return undefined;
    const el = rootRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const apply = () => {
      const w = el.clientWidth;
      if (w > 0) el.style.setProperty('--ifp-band-scale', String(w / DESIGN_WIDTH));
    };
    apply();
    const ro = new ResizeObserver(apply);
    ro.observe(el);
    return () => ro.disconnect();
  }, [placement]);

  const { isSupported, key, profile } = getIndustryProfile(industry);
  const scheme = getColorScheme(industry);
  const companyName = company?.trim() || (domain ? domain.split('.')[0].replace(/-/g, ' ') : 'Your Company');
  const headline = profile.heroHeading.replace('{company}', companyName);
  const locationLine = location ? `Serving ${location}` : 'Designed to convert ICP traffic into qualified conversations';
  const industryLabel = industry?.trim() || 'Business';
  const navLinks = ['Home', 'Services', 'Contact'];
  const industryIcon = INDUSTRY_ICON_MAP[key] || INDUSTRY_ICON_MAP.default;
  const brandFont = INDUSTRY_FONT_MAP[key] || INDUSTRY_FONT_MAP.default;

  return (
    <div
      ref={rootRef}
      className={`ifp-backdrop${visible ? ' is-visible' : ''}${mode === 'clear' ? ' is-clear' : ''}${placement === 'band' ? ' is-band' : ''}`}
      aria-hidden="true"
      style={{
        '--ifp-primary': scheme.primary,
        '--ifp-secondary': scheme.secondary,
        '--ifp-accent': scheme.accent,
        '--ifp-surface': scheme.surface,
        '--ifp-background': scheme.background,
        '--ifp-text': scheme.text,
      }}
    >
      <div className="ifp-shell">
        <div className="ifp-page">
          <div className="ifp-browser-bar">
            <span className="ifp-browser-dot" />
            <span className="ifp-browser-dot" />
            <span className="ifp-browser-dot" />
            <span className="ifp-browser-url">https://{domain || 'yourdomain.com'}</span>
          </div>

          <div className="ifp-site-header">
            <div className={`ifp-brand ifp-brand--${key}`}>
              <span className="ifp-brand-mark"><i className={`fa-solid ${industryIcon}`} aria-hidden="true" /></span>
              <span className="ifp-brand-name" style={{ fontFamily: brandFont }}>{companyName}</span>
            </div>
            <div className="ifp-nav">
              {navLinks.map((link) => (
                <span key={link}>{link}</span>
              ))}
              <span className="ifp-nav-cta">{profile.ctaText}</span>
            </div>
          </div>

          <section className="ifp-hero">
            <div className="ifp-hero-copy">
              <span className="ifp-badge">{industryLabel}</span>
              {!isSupported && <span className="ifp-badge ifp-badge-custom">Custom design on demand</span>}
              <h3>{headline}</h3>
              <p>{profile.heroSubheading}</p>
              <div className="ifp-location">{locationLine}</div>
              <div className="ifp-trust-row">
                {(profile.trustSignals || []).slice(0, 3).map((signal) => (
                  <span key={signal}>{signal}</span>
                ))}
              </div>
            </div>
            <div className="ifp-hero-form-card">
              <div className="ifp-form-card-header">Lead capture form</div>
              <div className="ifp-form-row" />
              <div className="ifp-form-row" />
              <div className="ifp-form-row" />
              <div className="ifp-form-row is-tall" />
              <div className="ifp-form-button">{profile.ctaText}</div>
            </div>
          </section>

          <section className="ifp-features">
            {profile.features.slice(0, 3).map((feature) => (
              <article key={feature.title} className="ifp-feature-card">
                <span className="ifp-feature-icon">{feature.icon}</span>
                <h4>{feature.title}</h4>
                <p>{feature.description}</p>
              </article>
            ))}
          </section>

          <section className="ifp-contact">
            <div className="ifp-contact-copy">
              <div className="ifp-section-kicker">Contact</div>
              <h4>Fast, ICP-ready landing page with a built-in inquiry flow</h4>
              <p>The final page can be customized around your niche, offer, and outbound traffic source.</p>
            </div>
            <div className="ifp-contact-form">
              <div className="ifp-input-label">Full name</div>
              <div className="ifp-input-line" />
              <div className="ifp-input-label">Email</div>
              <div className="ifp-input-line" />
              <div className="ifp-input-label">Phone</div>
              <div className="ifp-input-line" />
              <div className="ifp-input-label">How can we help?</div>
              <div className="ifp-input-line is-area" />
            </div>
          </section>

          <div className="ifp-footer">
            <span>{companyName}</span>
            <span>Powered by {BRAND.name}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default IcpFormPreview;
